<template>
  <SlideInLayer
    variant="filters"
    :class="{ '[&_#footer]:border-t': elementIsOverflown }"
    :headerVariant="!currentFilter ? 'filters_from' : 'filters_to'"
    :open="isOpen"
    :title="t('admin.filters.header.title')"
    :description="description"
    @open="handleOpen"
    @close="handleClose"
  >
    <template v-if="$slots.default" #trigger>
      <slot />
    </template>
    <template v-if="showBackInHeader || component?.showSearch" #header-end>
      <BackAndSearch
        class="pb-2"
        v-model:query="query"
        :showBack="showBackInHeader"
        :showSearch="component?.showSearch"
        @back="component?.handleBack()"
      />
    </template>
    <template #content>
      <div class="relative flex flex-col grow overflow-y-auto">
        <div class="h-full">
          <FilterList
            v-if="!currentFilter"
            :filters="filterList"
            :activeFilters="Object.keys(filterStates)"
            @filterSelected="handleFilterSelected"
            class="py-4"
          />
          <div v-else class="h-full flex flex-col grow">
            <Component
              :is="filterMapping[currentFilter].component"
              :initialState="currentFilterState"
              :query="query"
              @apply="handleApplyFilter"
              @isLoaded="handleIsOverflown"
              class="[&>div]:py-4"
              ref="component"
              v-bind="filterMapping[currentFilter].props"
            />
          </div>
        </div>
      </div>
    </template>
    <template
      v-if="
        (maxSmall && !currentFilter && hasActiveFilters) ||
        showBackInFooter ||
        component?.showApply
      "
      #footer
    >
      <ClearFilters
        v-if="maxSmall && !currentFilter && hasActiveFilters"
        @click="handleClearFilters"
      />
      <ApplyOrBack
        v-else
        modal="sheet"
        :showBack="showBackInFooter"
        :showApply="component?.showApply"
        :disableApply="component?.disableApply"
        @back="component?.handleBack()"
        @apply="component?.handleApply()"
      />
    </template>
  </SlideInLayer>
</template>

<script setup>
import { cloneDeep } from "lodash";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue";
import { SlideInLayer } from "@/Components/Sheet";
import { FilterList, FilterType, filters } from "@/Components/Filters";
import { BackAndSearch } from "@/Components";
import { ApplyOrBack, ClearFilters } from "@/Components/Buttons";
import { Status } from "@/Components/Status";
import { LinkedTo } from "@/Components/LinkedTo";
import { Users } from "@/Components/Users";
import { Date } from "@/Components/Date";
import { Visibility } from "@/Components/Visibility";
import { Priority } from "@/Components/Priority";
import { Categories } from "@/Components/Categories";
import { Tags } from "@/Components/Tags";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useOverflow } from "@/Composables/useOverflow";
import { useFilters } from "@/Composables/useFilters";

const { checkIfOverflown, elementIsOverflown } = useOverflow();

const handleIsOverflown = () => {
  checkIfOverflown(currentFilter.value, component.value);
};

const emit = defineEmits(["close", "apply", "clear"]);

const { maxSmall } = useBreakpoints();
const component = ref(null);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  filters: { type: Array, required: true },
  filterStates: { type: Object, default: () => ({}) },
  customFilterStates: { type: Object, default: () => ({}) },
  disabledFilters: { type: Array, default: () => [] },
  initialFilter: { type: String, default: null },
});

const { t } = useI18n();

const isOpen = ref(props.open);
const currentFilter = ref(null);
const query = ref("");

const filterStore = useFilters("global");
const { filterStates: globalFilterState } = storeToRefs(filterStore);

const filterList = computed(() =>
  filters
    .filter((f) => props.filters.find((filter) => filter.type === f.id))
    .map((f) => ({
      ...f,
      active: isActive(f.id),
      disabled: shouldDisable(f.id),
    }))
);

onMounted(() => {
  window.addEventListener("resize", handleIsOverflown);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleIsOverflown);
});

watchEffect(() => {
  if (props.open) {
    handleOpen();
  } else {
    isOpen.value = props.open;
  }
});

function handleFilterSelected(id) {
  currentFilter.value = id;
}

function isActive(filterType) {
  return Object.keys(props.customFilterStates).includes(filterType);
}

function shouldDisable(filterType) {
  return (
    props.disabledFilters.includes(filterType) ||
    !filterMapping.value[filterType]
  );
}

const hasActiveFilters = computed(() => {
  return Object.keys(props.customFilterStates).length > 0;
});

const currentFilterState = computed(() => {
  return cloneDeep(props.customFilterStates[currentFilter.value]);
});

const description = computed(() => {
  return t(
    filters.find((filter) => filter.id === currentFilter.value)?.label ||
      "admin.filters.header.description"
  );
});

const showBackInHeader = computed(() => {
  return (
    component.value?.showBack &&
    !filterMapping.value[currentFilter.value]?.isDate
  );
});

const showBackInFooter = computed(() => {
  return (
    component.value?.showBack &&
    filterMapping.value[currentFilter.value]?.isDate
  );
});

const filterMapping = computed(() => ({
  [FilterType.STATUS]: {
    component: Status,
  },
  [FilterType.LINKED_TO]: {
    component: LinkedTo,
    props: {
      ...props.filters.find((f) => f.type === FilterType.LINKED_TO)?.props,
      siteId: globalFilterState.value[FilterType.LINKED_TO]?.sites[0],
      query: query.value,
    },
  },
  [FilterType.ASSIGNED_TO]: {
    component: Users,
    props: {
      query: query.value,
    },
  },
  [FilterType.EXPIRATION_DATE]: {
    component: Date,
    props: {
      isRange: true,
    },
    isDate: true,
  },
  [FilterType.NOTIFICATION_DATE]: {
    component: Date,
    props: {
      isRange: true,
    },
    isDate: true,
  },
  [FilterType.DUE_DATE]: {
    component: Date,
    props: {
      isRange: true,
    },
    isDate: true,
  },
  [FilterType.VISIBILITY]: {
    component: Visibility,
  },
  [FilterType.PRIORITY]: {
    component: Priority,
  },
  [FilterType.CATEGORY]: {
    component: Categories,
    props: {
      query: query.value,
    },
  },
  [FilterType.TAGS]: {
    component: Tags,
    props: {
      query: query.value,
    },
  },
}));

function handleApplyFilter(filterState) {
  emit("apply", { filterType: currentFilter.value, filterState });
}

function handleClearFilters() {
  emit("clear");
  handleClose();
}

function handleOpen() {
  isOpen.value = true;
  currentFilter.value = props.initialFilter;
}

function handleClose() {
  if (props.open === undefined) {
    isOpen.value = false;
  } else {
    emit("close");
  }
}
</script>
